// react
import { useCallback, useMemo, useState } from 'react';

// theme
import styled from 'styled-components';

// components
import { Button } from '@/components/atoms/Button/Button';
import { Divider } from '@/components/atoms/Divider/Divider';
import { Input } from '@/components/atoms/Input/Input';
import { SupportingText } from '@/components/atoms/SupportingText/SupportingText';

import { RowAction, Table } from '../Table/Table';
import { UploadFileButton } from '@/components/molecules/UploadFileButton/UploadFileButton';

// tanstack
import { createColumnHelper } from '@tanstack/react-table';

// models
import { AddInviteKeys, Organization } from '@karehero/models';

interface MemberDetails {
  inviteKey: string;
}

// columns
const columnHelper = createColumnHelper<MemberDetails>();

/**
 * InviteMembersForm is a component that renders password and confirm password input field with
 * a submit button.
 */
export const InviteMembersForm = ({
  inputType,
  inviteKeyName,
  onSubmit,
  isValid,
}: InviteMembersFormProps) => {
  // state
  const [memberDetails, setMemberDetails] = useState<MemberDetails[]>([]);
  const [newMember, setNewMember] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingInviteKey, setEditingInviteKey] = useState<string>('');
  const initialInviteKey =
    editingIndex !== null ? memberDetails[editingIndex].inviteKey : '';

  // callbacks
  const cancelEditing = useCallback(() => {
    setEditingIndex(null);
    setEditingInviteKey('');
  }, []);

  const editMember = useCallback(
    (memberIndex: number | null, inviteKey: string) => {
      if (memberIndex === null) return;

      setMemberDetails((prev) => [
        ...prev.slice(0, memberIndex),
        { ...prev[memberIndex], inviteKey },
        ...prev.slice(memberIndex + 1),
      ]);
    },
    [],
  );

  const addMember = useCallback((inviteKey: string) => {
    if (!isValid(inviteKey)) return;
    setMemberDetails((prev) => {
      if (prev.findIndex((md) => md.inviteKey === inviteKey) > -1) return prev;
      return [...prev, { inviteKey }];
    });
  }, []);

  const addMembers = useCallback((inviteKeys: string[]) => {
    setMemberDetails((prev) => {
      const newMembers = inviteKeys.filter(
        (inviteKey) =>
          prev.findIndex((md) => md.inviteKey === inviteKey) === -1,
      );
      const invalidInviteKeys = newMembers.filter(
        (inviteKey) => !isValid(inviteKey),
      );
      const validInviteKeys = newMembers.filter((inviteKey) =>
        isValid(inviteKey),
      );

      return [
        ...invalidInviteKeys.map((inviteKey) => ({ inviteKey })),
        ...prev,
        ...validInviteKeys.map((inviteKey) => ({ inviteKey })),
      ];
    });
  }, []);

  const importFileContent = useCallback(
    (content: string) => {
      addMembers(
        content
          .split('\n')
          .map((e) => e.split(',')[0])
          .filter((e) => e.length > 0),
      );
    },
    [addMembers],
  );

  // memo
  const invalidCount = useMemo(() => {
    return memberDetails.filter((member) => !isValid(member.inviteKey)).length;
  }, [memberDetails]);

  const memberCount = useMemo(() => memberDetails.length, [memberDetails]);

  const Editor = useMemo(() => {
    return () => {
      const [val, setVal] = useState(initialInviteKey);

      return (
        <Input
          ariaLabel={`edit ${inviteKeyName}`}
          size='sm'
          placeholder={`Edit ${inviteKeyName}`}
          value={val}
          type={inputType}
          onChange={(v) => {
            setVal(v || '');
            setEditingInviteKey(v || '');
          }}
          autoFocus
        />
      );
    };
  }, [initialInviteKey]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('inviteKey', {
        cell: (info) => {
          const val = info.getValue();
          return editingIndex === info.row.index ? (
            <Editor />
          ) : isValid(val) ? (
            val
          ) : (
            <StyledInvalidInviteKey>{val}</StyledInvalidInviteKey>
          );
        },
      }),
    ];
  }, [editingIndex, Editor]);

  const actions = useMemo<RowAction[]>(() => {
    return [
      {
        label: '',
        iconLeft: 'edit',
        buttonVariant: 'link',
        isHidden: (member: MemberDetails) => editingIndex !== null,
        onPress: (member: MemberDetails) => {
          setEditingIndex(
            memberDetails.findIndex((md) => md.inviteKey === member.inviteKey),
          );
          setEditingInviteKey(member.inviteKey);
        },
      },
      {
        label: '',
        iconLeft: 'trash',
        buttonVariant: 'link',
        isHidden: (member: MemberDetails) =>
          editingIndex ===
          memberDetails.findIndex((md) => md.inviteKey === member.inviteKey),
        onPress: (member: MemberDetails) => {
          cancelEditing();
          setMemberDetails(
            memberDetails.filter((md) => md.inviteKey !== member.inviteKey),
          );
        },
      },
      {
        label: '',
        iconLeft: 'check',
        buttonVariant: 'link',
        isHidden: (member: MemberDetails) =>
          editingIndex !==
          memberDetails.findIndex((md) => md.inviteKey === member.inviteKey),
        onPress: () => {
          editMember(editingIndex, editingInviteKey);
          cancelEditing();
        },
      },
      {
        label: '',
        iconLeft: 'xmark',
        buttonVariant: 'link',
        isHidden: (member: MemberDetails) =>
          editingIndex !==
          memberDetails.findIndex((md) => md.inviteKey === member.inviteKey),
        onPress: () => {
          cancelEditing();
        },
      },
    ];
  }, [
    memberDetails,
    editingInviteKey,
    editingIndex,
    editMember,
    cancelEditing,
  ]);

  // effects

  return (
    <StyledInviteMembersForm
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <StyledTopSection>
        <SingleInvite>
          <Input
            ariaLabel={`add ${inviteKeyName}`}
            placeholder={`Add ${inviteKeyName}`}
            value={newMember}
            size='sm'
            type={inputType}
            onChange={(val) => setNewMember(val || '')}
          />
          <Button
            ariaLabel='add'
            type='submit'
            isRectangle
            size='sm'
            onPress={() => {
              addMember(newMember);
              setNewMember('');
            }}
            isDisabled={!isValid(newMember)}
          >
            Add
          </Button>
        </SingleInvite>
        <Divider />
        <UploadFileButton
          onChange={importFileContent}
          ariaLabel='upload csv'
          size='md'
        >
          Upload CSV
        </UploadFileButton>
      </StyledTopSection>
      <StyledTable
        columns={columns}
        rows={memberDetails}
        actions={actions}
        isHeader={false}
        isSearchable={false}
        isPaginated={false}
        isBorder={true}
        isTotalRows={false}
        noDataMsg={`No ${inviteKeyName}s added yet`}
      />
      {invalidCount > 0 && (
        <StyledSupportingText
          text={
            invalidCount === 1
              ? `${invalidCount} ${inviteKeyName} is invalid`
              : `${invalidCount} ${inviteKeyName}s are invalid`
          }
        />
      )}
      <ButtonWrapper>
        <StyledButton
          ariaLabel='invite members'
          isDisabled={memberCount === 0 || invalidCount > 0}
          onPress={() =>
            onSubmit({
              inviteKeys: memberDetails.map((md) => md.inviteKey),
            })
          }
        >
          {`Invite ${memberCount > 0 ? `${memberCount}` : ''} Member${
            memberCount > 1 ? 's' : ''
          }`}
        </StyledButton>
      </ButtonWrapper>
    </StyledInviteMembersForm>
  );
};

const StyledInviteMembersForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: ${(props) => props.theme.spacing[24]};
  gap: ${(props) => props.theme.spacing[16]};
  overflow: hidden;
`;

const StyledTopSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: min-content;
  gap: ${(props) => props.theme.spacing[16]};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: ${(props) => props.theme.spacing[16]};
  overflow: hidden;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledInvalidInviteKey = styled.div`
  color: red;
`;

const SingleInvite = styled.div`
  display: flex;
  width: 100%;
  gap: ${(props) => props.theme.spacing[8]};
`;

// main
const StyledTable = styled(Table)`
  overflow-y: auto;
  width: 100%;
  padding: ${(props) => props.theme.spacing[16]};
`;

const StyledSupportingText = styled(SupportingText)`
  width: 100%;
  color: red;
`;

interface InviteMembersFormProps {
  organization?: Organization;
  onSubmit: (req: AddInviteKeys) => void;
  isValid: (value: string) => boolean;
  inputType: 'email' | 'text';
  inviteKeyName: string;
}

InviteMembersForm.displayName = 'InviteMembersForm';

export default InviteMembersForm;
